import { State } from 'xstate'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyState = State<any, any, any, any, any>

/**
 * Логирование машины включается через .env:
 *   REACT_APP_LOG_RP_MACHINE=true
 * По умолчанию — выключено.
 */
const isEnabled =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_LOG_RP_MACHINE === 'true'

// ─── Helpers ──────────────────────────────────────────────

/** { joined: { playing: { dealing: 'animating' } } } → 'joined.playing.dealing.animating' */
function flattenState(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value !== null) {
    return Object.entries(value)
      .map(([k, v]) => `${k}.${flattenState(v)}`)
      .join(' | ')
  }
  return String(value)
}

/** Имена guards из взятых переходов (только те, у которых было условие) */
function getGuards(state: AnyState): string[] {
  return state.transitions
    .filter((t: any) => t.cond != null)
    .map((t: any) => {
      const { cond } = t
      // named guard: { type: 'hasCardInQueue' }
      if (typeof cond === 'object' && cond.type) return `✓ ${cond.type}`
      // inline function
      if (typeof cond === 'function') return `✓ ${cond.name || 'anonymous'}`
      return '✓ ?'
    })
}

/** XState internal actions (assign, xstate.after...) отфильтровываем */
function getActions(state: AnyState): string[] {
  return state.actions
    .map((a: any) => a.type as string)
    .filter((t) => !t.startsWith('xstate.'))
}

// ─── Logger ───────────────────────────────────────────────

export const logRpMachineState = (state: AnyState): void => {
  if (!isEnabled) return

  const { changed, event } = state
  const { type: eventType, ...eventPayload } = event

  const from = flattenState(state.history?.value ?? '∅')
  const to = flattenState(state.value)
  const guards = getGuards(state)
  const actions = getActions(state)
  const hasPayload = Object.keys(eventPayload).length > 0

  if (!changed) {
    // eslint-disable-next-line no-console
    console.log(
      `%c RP %c ${eventType} %c⊘ ignored in ${to}`,
      'background:#7c3aed;color:#fff;font-weight:bold;padding:1px 6px;border-radius:3px',
      'color:#6b7280',
      'color:#ef4444',
    )
    return
  }

  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `%c RP %c ${from} %c→%c ${to}`,
    'background:#7c3aed;color:#fff;font-weight:bold;padding:1px 6px;border-radius:3px 0 0 3px',
    'background:#1e1b4b;color:#a5b4fc;padding:1px 8px',
    'color:#7c3aed;font-weight:bold;padding:1px 4px',
    'background:#1e1b4b;color:#c4b5fd;font-weight:bold;padding:1px 8px;border-radius:0 3px 3px 0',
  )

  const s = 'color:#64748b'
  // eslint-disable-next-line no-console
  console.log('%cevent  ', s, eventType)

  if (hasPayload) {
    // eslint-disable-next-line no-console
    console.log('%cpayload', s, eventPayload)
  }

  if (guards.length > 0) {
    // eslint-disable-next-line no-console
    console.log('%cguards ', s, guards.join('  '))
  }

  if (actions.length > 0) {
    // eslint-disable-next-line no-console
    console.log('%cactions', s, actions)
  }

  // eslint-disable-next-line no-console
  console.log('%ccontext', s, state.context)
  // eslint-disable-next-line no-console
  console.groupEnd()
}
